import { generateId } from '@vedalib/editor/lib/utils/id'

import type { ScormRuntimeVersion } from '../transport/common'
import { DEFAULT_SUSPEND_DATA, SCORM_API_Wrapper } from '../transport/scorm-client'
import type { ScormSuspendData, IInteractionData } from '../transport/types'
import type {
  CompletionStatus,
  ILmsEvent,
  IProviderConfig,
  IScore,
  LmsType,
  SuccessStatus,
} from '../types'
import { BaseLmsProvider } from './base.provider'
import { requireConnection, requireConnectionOrNull } from './decorators'

export class ScormProvider extends BaseLmsProvider {
  readonly type: LmsType = 'scorm'

  async initialize(config: IProviderConfig): Promise<boolean> {
    this.config = config

    // SCORM init is handled externally before provider creation
    // We just check if already connected
    if (!SCORM_API_Wrapper.isConnected()) {
      await SCORM_API_Wrapper.init(false)
    }

    if (!SCORM_API_Wrapper.isConnected()) {
      console.warn('[ScormProvider] SCORM API not connected')
      return false
    }

    this.connected = true

    // Extract learner info from SCORM
    const scormLearner = SCORM_API_Wrapper.getLearnerInfo()
    const version = SCORM_API_Wrapper.getVersion()

    // Generate or restore xapiSessionId from suspend_data
    let xapiSessionId: string | undefined
    const suspendData = await this.getSuspendData()

    if (suspendData?.xapiSessionId) {
      xapiSessionId = suspendData.xapiSessionId
    } else {
      xapiSessionId = generateId()
      await this.setSuspendData({ ...(suspendData || DEFAULT_SUSPEND_DATA), xapiSessionId })
      await this.commit()
    }

    this.setLearnerInfo({
      learnerId: scormLearner.learnerId || undefined,
      learnerName: scormLearner.learnerName || undefined,
      lmsType: 'scorm',
      lmsVersion: version,
      sessionId: xapiSessionId,
    })

    return true
  }

  @requireConnection
  async terminate(): Promise<void> {
    await SCORM_API_Wrapper.terminate({ mode: 'exit', closeWindow: true })
    this.connected = false
    console.info('[ScormProvider] Terminated')
  }

  @requireConnection
  async setSuspendData(data: ScormSuspendData): Promise<void> {
    SCORM_API_Wrapper.setSuspendData(data)
  }

  @requireConnectionOrNull
  async getSuspendData(): Promise<ScormSuspendData | null> {
    return SCORM_API_Wrapper.getSuspendData()
  }

  // Синхронный вариант: SCORM_API_Wrapper.getSuspendData() читает из кэша
  // dataStorage (уже прогрет через ScormInitializedRoutes → readOnInit).
  // Используется в useReduxUpdate для единственного dispatch(init) без await.
  getSuspendDataSync(): ScormSuspendData | null {
    if (!SCORM_API_Wrapper.isConnected()) {
      return null
    }
    return SCORM_API_Wrapper.getSuspendData()
  }

  @requireConnection
  async setProgress(value: number): Promise<void> {
    const measure = (value / 100).toString()
    SCORM_API_Wrapper.setValue('cmi.progress_measure', measure)
  }

  @requireConnection
  async setScore(score: IScore): Promise<void> {
    SCORM_API_Wrapper.setValue('cmi.score.min', score.min.toString())
    SCORM_API_Wrapper.setValue('cmi.score.max', score.max.toString())
    SCORM_API_Wrapper.setValue('cmi.score.raw', score.raw.toString())
    SCORM_API_Wrapper.setValue('cmi.score.scaled', score.scaled.toString())
  }

  @requireConnection
  async setCompletionStatus(status: CompletionStatus): Promise<void> {
    SCORM_API_Wrapper.setValue('cmi.completion_status', status)
  }

  @requireConnection
  async setSuccessStatus(status: SuccessStatus): Promise<void> {
    SCORM_API_Wrapper.setValue('cmi.success_status', status)
  }

  @requireConnection
  async setLocation(location: string): Promise<void> {
    SCORM_API_Wrapper.setValue('cmi.location', location, true) // force immediate save
  }

  async trackEvent(_event: ILmsEvent): Promise<void> {
    // SCORM doesn't support xAPI-style events
    // Interactions are handled separately through setInteraction
  }

  @requireConnection
  async commit(): Promise<void> {
    SCORM_API_Wrapper.commit()
  }

  setInteraction(index: number, data: IInteractionData): void {
    if (!this.connected) {
      return
    }
    SCORM_API_Wrapper.setInteraction(index, data)
  }

  getScormVersion(): ScormRuntimeVersion {
    return SCORM_API_Wrapper.getVersion()
  }
}
